import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { RouteComponentProps } from 'react-router'
import { Column, useColumnOrder, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { Grid } from '@material-ui/core'

import { ExportButton, ExtendedPagedTable, useFiltersFromQuery } from 'core/table/components'
import { useGetColumnState, useTableColumnStyles as useStyles } from 'core/table/table-hooks/'
import { MessagesServerSideSelectionProvider } from 'messages/context/MessagesServerSideSelectionContext'
import { useMessagesServerSideSelectionPlugin } from 'messages/hooks/useMessagesServerSideSelectionPlugin'
import { useServerSideMessagesRowSelection } from 'messages/hooks/useServerSideMessagesRowSelection'
import { useServerSideMessagesSelectColumn } from 'messages/hooks/useServerSideMessagesSelectColumn'

import Loading from 'components/Loading'

import { useAutomatedMessagesColumns, useFetchAutomatedMessages } from '../hooks'
import { AutomatedMessage } from '../types'
import { useDateRangeFilter } from 'filtering/hooks'
import {
  AUTOMATED_MESSAGES_CSV_HEADERS,
  AUTOMATED_MESSAGES_DATE_RANGE_KEY,
  AUTOMATED_MESSAGES_TABLE_NAME,
  MESSAGES_LIST_DEFAULT_DATE_RANGE,
  getAutomatedMessagesOperatorOptions,
} from './automatedMessagesConfig'

export const AutomatedMessagesServerSide = ({ location, history }: RouteComponentProps) => {
  const skipReset = React.useRef<boolean>()
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const columnStyles = useStyles()
  const [dateRange] = useDateRangeFilter({
    key: AUTOMATED_MESSAGES_DATE_RANGE_KEY,
    defaultDateRange: MESSAGES_LIST_DEFAULT_DATE_RANGE,
  })

  const {
    messages,
    totalCount,
    loading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    onSearchChange,
    filterKey,
    fetchAllMatchingMessages,
  } = useFetchAutomatedMessages(dateRange)

  const columns = useAutomatedMessagesColumns()
  useFiltersFromQuery(location.search, getAutomatedMessagesOperatorOptions)

  const pageCount = totalCount === 0 ? 0 : Math.ceil(totalCount / pageSize)
  const tableDispatchRef = useRef<(action: { type: string; selectedRowIds?: Record<string, boolean> }) => void>(
    () => undefined,
  )

  const {
    selectedRowIds,
    selectedCount,
    selectingAll,
    selectionPluginApiRef,
    clearSelection,
    selectAllMatching,
    getSelectedRows,
  } = useServerSideMessagesRowSelection({
    rows: messages,
    getRowId: (row: AutomatedMessage) => row.groupKey,
    totalCount,
    listLoading: loading,
    fetchAllMatching: fetchAllMatchingMessages,
  })

  const messagesSelectionPlugin = useMessagesServerSideSelectionPlugin(selectionPluginApiRef)

  const selectionContextValue = useMemo(
    () => ({
      totalCount,
      selectedCount,
      selectingAll,
      listLoading: loading,
      onSelectAllMatching: () => selectAllMatching(tableDispatchRef.current),
      onClearSelection: () => clearSelection(tableDispatchRef.current),
    }),
    [clearSelection, loading, selectAllMatching, selectedCount, selectingAll, totalCount],
  )

  const table = useTable(
    {
      data: messages,
      columns: columns as Column<AutomatedMessage>[],
      initialState: {
        pageIndex,
        pageSize,
        sortBy: [{ id: 'createdAt', desc: true }],
        selectedRowIds,
      },
      manualPagination: true,
      pageCount,
      autoResetPage: false,
      autoResetSelectedRows: false,
      getRowId: (row: AutomatedMessage) => row.groupKey,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    messagesSelectionPlugin,
    useServerSideMessagesSelectColumn(() => true, columnStyles.selectColumn),
  )

  tableDispatchRef.current = table.dispatch

  useEffect(() => {
    clearSelection(tableDispatchRef.current)
  }, [filterKey, clearSelection])

  useEffect(() => {
    fetchColumnState(AUTOMATED_MESSAGES_TABLE_NAME)
  }, [fetchColumnState])

  useEffect(() => {
    if (table.state.pageIndex !== pageIndex) {
      table.gotoPage(pageIndex)
    }
  }, [pageIndex, table])

  useEffect(() => {
    if (table.state.pageSize !== pageSize) {
      table.setPageSize(pageSize)
    }
  }, [pageSize, table])

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageIndex(newPage)
    },
    [setPageIndex],
  )

  const selectedRows = useMemo(() => getSelectedRows(), [getSelectedRows, selectedCount])
  const disabled = selectedCount === 0

  if (columnStateLoading || (loading && messages.length === 0)) {
    return <Loading />
  }

  return (
    <MessagesServerSideSelectionProvider value={selectionContextValue}>
      <Grid container>
        <ExtendedPagedTable<AutomatedMessage>
          history={history}
          location={location}
          table={table}
          columns={columns}
          columnState={columnState}
          tableName={AUTOMATED_MESSAGES_TABLE_NAME}
          searchPlaceholder="Search Messages"
          skipReset={skipReset}
          buttons={
            <>
              <ExportButton
                disabled={disabled}
                data={selectedRows}
                headers={AUTOMATED_MESSAGES_CSV_HEADERS}
                filename={'automated-messages.csv'}
              />
            </>
          }
          useGlobalSearch
          useDateRange
          customDateKey={AUTOMATED_MESSAGES_DATE_RANGE_KEY}
          defaultDateRange={MESSAGES_LIST_DEFAULT_DATE_RANGE}
          serverSide
          totalCount={totalCount}
          pageIndex={pageIndex}
          onPageChange={handlePageChange}
          onPageSizeChange={setPageSize}
          onSearchChange={onSearchChange}
        />
      </Grid>
    </MessagesServerSideSelectionProvider>
  )
}
